// Must stay the first import: it has to evaluate before every other module.
// See bootRedirect.ts for why inline code here would run too late.
import './bootRedirect'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'
import * as Sentry from "@sentry/react";
import { AnalyticsProvider } from './analytics.tsx'

if (import.meta.env.PROD) {
  Sentry.init({
    dsn: import.meta.env.SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? 'production',
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    integrations: [Sentry.browserTracingIntegration()],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", "https://double-bagel-9f766.web.app", "https://double-bagel-9f766.firebaseapp.com", "https://double-bagel-staging.web.app", "http://localhost:5173", "https://doublebagelapp.com"],
    beforeSend(event) {
      // Service worker registration can fail in incognito mode, WebViews,
      // or when the user navigates away before the SW installs — non-fatal.
      const isSwError = event.exception?.values?.some((ex) =>
        ex.stacktrace?.frames?.some((f) => f.filename?.includes('registerSW'))
      );
      if (isSwError) return null;
      return event;
    },
  });
}

// After a deploy, lazy-loaded chunks from the old bundle 404. Vite fires this
// event when that happens — silently reloading gets users onto the new version.
// Guard with sessionStorage to prevent an infinite loop if the SW still serves
// the stale bundle after the reload (clears on tab close for fresh retries).
window.addEventListener('vite:preloadError', () => {
  if (sessionStorage.getItem('preload-err-reload')) return;
  sessionStorage.setItem('preload-err-reload', '1');
  window.location.reload();
});

// ── Deliberate reload when a new service worker takes control ────────────────
// The worker is built with skipWaiting + clientsClaim (vite.config.ts), so a new
// worker activates and claims this page as soon as it installs. Claiming changes
// which assets are SERVED; it does not change the JavaScript already RUNNING.
// Without this listener the page limps along on the old bundle until it happens
// to request a lazy chunk that is gone and falls into vite:preloadError above.
//
// The two reloads cannot loop each other: they hold separate sessionStorage
// keys, and this one normally fires first (claiming happens on activation, well
// before the next chunk request), so preloadError stops being the trigger.
//
// The key stores the build id this page was RUNNING when it last reloaded for a
// worker takeover, not a bare '1'. Keying it to the tab meant one automatic
// reload per tab for the life of that tab — every later deploy was ignored and
// the tab ran stale code indefinitely. Comparing against the running build
// instead still breaks a loop (a reload that lands back on the same bundle is
// refused) while allowing one reload per deploy.
const SW_RELOAD_KEY = 'sw-reload';

// Build id injected by versionJsonPlugin in vite.config.ts — the same value
// useVersionCheck compares against /version.json. Declared locally because the
// project has no shared ambient .d.ts; useVersionCheck.ts declares it the same
// way.
declare const __APP_VERSION__: string;

// True when this page already reloaded for a worker takeover AND is still on
// the same bundle it was running then, i.e. the reload did not move us forward.
const alreadyReloadedThisBuild = () =>
  sessionStorage.getItem(SW_RELOAD_KEY) === __APP_VERSION__;

// Read at module evaluation, before any worker can claim this page. A null
// controller means nothing was controlling us at load — a first install rather
// than an update, where reloading would only replay the bundle we already have.
const hadControllerAtLoad =
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator &&
  navigator.serviceWorker.controller !== null;

// Work that must not be interrupted, published as <body> attributes by the
// components that own it: LessonVideoEditor sets data-editor-open while it is
// mounted, PendingUploadsIndicator sets data-upload-active while a transfer is
// in flight. Attributes rather than shared state because this file is outside
// the React tree.
const RELOAD_BLOCKING_ATTRS = ['data-editor-open', 'data-upload-active'];
const isReloadBlocked = () =>
  RELOAD_BLOCKING_ATTRS.some((attr) => document.body.hasAttribute(attr));

// How long to let a deferred reload settle once nothing is blocking it. The
// attribute clears the instant an upload finishes or the editor closes, so
// reloading on that edge drops a refresh on top of the screen the user has only
// just landed on. Waiting lets them see the result first. Deferred path only —
// a controllerchange with nothing blocking still reloads immediately.
const RELOAD_SETTLE_MS = 3000;

if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  let blockedObserver: MutationObserver | null = null;
  let settleTimer: ReturnType<typeof setTimeout> | null = null;

  const performReload = () => {
    if (alreadyReloadedThisBuild()) return;
    blockedObserver?.disconnect();
    blockedObserver = null;
    sessionStorage.setItem(SW_RELOAD_KEY, __APP_VERSION__);
    window.location.reload();
  };

  // Runs on every change to the two blocking attributes, and only ever on the
  // deferred path — the observer is armed solely when a reload was held back.
  const onBlockingAttrsChanged = () => {
    // Blocking work resumed (a second upload started, the editor reopened)
    // inside the settle window: drop the timer and go back to waiting on the
    // observer, which stays connected.
    if (isReloadBlocked()) {
      if (settleTimer) {
        clearTimeout(settleTimer);
        settleTimer = null;
      }
      return;
    }
    // Already counting down. Both attributes were clear when the timer started,
    // so a further clearing mutation must not restart it.
    if (settleTimer) return;
    settleTimer = setTimeout(() => {
      settleTimer = null;
      // Re-check rather than trusting the timer. In practice a re-block would
      // have cancelled it above, since MutationObserver records are delivered
      // as microtasks long before this fires — but if it somehow did not, the
      // observer is still connected and the next clear starts a fresh timer,
      // so the update is deferred again rather than lost.
      if (isReloadBlocked()) return;
      performReload();
    }, RELOAD_SETTLE_MS);
  };

  const reloadForNewWorker = () => {
    if (alreadyReloadedThisBuild()) return;

    // Blocked: keep the pending update rather than dropping it, and retry once
    // both attributes are gone and RELOAD_SETTLE_MS has passed. A
    // MutationObserver is used in place of polling because the attributes are
    // written synchronously in effect cleanups — the countdown starts in the
    // same task that finishes the work, and there is no interval left running
    // for the rest of the session.
    if (isReloadBlocked()) {
      if (!blockedObserver) {
        blockedObserver = new MutationObserver(onBlockingAttrsChanged);
        blockedObserver.observe(document.body, { attributeFilter: RELOAD_BLOCKING_ATTRS });
      }
      return;
    }

    // Nothing blocking at the moment the worker took over: reload immediately.
    performReload();
  };

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadControllerAtLoad) return;
    reloadForNewWorker();
  });
}

// In dev, defensively unregister any service worker left over from a prior prod
// build. A stale SW caching old bundles is a common cause of "blank page on hard
// refresh" and "0 results on first nav" symptoms in dev.
if (import.meta.env.DEV && typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((regs) => {
    regs.forEach((reg) => reg.unregister());
  }).catch(() => { /* non-fatal */ });
}

createRoot(document.getElementById('root')!).render(
  <AnalyticsProvider>
    <App />
  </AnalyticsProvider>,
)
